import type { PitchSessionStatus } from '@workspace/api-backend'
import { Button } from '@workspace/ui/components/button'
import { PitchCard } from '@workspace/ui/components/pitch-card'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { cn } from '@workspace/ui/lib/utils'
import { AlertTriangle, Loader2, Play, Video, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { MarkdownContent } from '@/components/markdown-content'
import { DocumentPreviewModal, type PreviewDocument } from '@/features/preview'
import { useGetPitchVideoUrlMutation } from '@/hooks/api'
import type { PitchSessionDetailProps } from '../types/pitch.types'
import { extractEvaluationData } from '../utils/scores'
import { JudgesAccordion } from './judges-accordion'
import { MetricsTabs } from './metrics-tabs'
import { PitchAnalysisTabs } from './pitch-analysis-tabs'
import { VideoPreviewModal } from './video-preview-modal'

type StatusVariant = 'orange' | 'red' | 'amber' | 'blue'

const variantStyles: Record<StatusVariant, { border: string; bg: string; iconBg: string }> = {
  orange: {
    border: 'border-border',
    bg: 'bg-input/50',
    iconBg: 'bg-[#f97316]/10',
  },
  red: {
    border: 'border-red-500/20',
    bg: 'bg-red-500/5',
    iconBg: 'bg-red-500/10',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/5',
    iconBg: 'bg-amber-500/10',
  },
  blue: {
    border: 'border-blue-500/20',
    bg: 'bg-blue-500/5',
    iconBg: 'bg-blue-500/10',
  },
}

type StatusKey = Exclude<PitchSessionStatus, 'COMPLETED'> | 'PROCESSING' | 'EVALUATION_FAILED'

const statusConfig: Record<
  StatusKey,
  { icon: ReactNode; title: string; description: string; variant: StatusVariant }
> = {
  PENDING: {
    icon: <Loader2 className="size-6 text-[#f97316] animate-spin" />,
    title: 'Preparing Your Pitch Session',
    description: 'Analyzing your pitch deck and preparing your AI investor.',
    variant: 'orange',
  },
  ACTIVE: {
    icon: <Video className="size-6 text-amber-400" />,
    title: 'Ready to Join',
    description: 'Your pitch session is ready. Join when you are prepared.',
    variant: 'amber',
  },
  IN_PROGRESS: {
    icon: <Play className="size-6 text-blue-400" />,
    title: 'Session In Progress',
    description: 'Your pitch session is currently active.',
    variant: 'blue',
  },
  PROCESSING: {
    icon: <Loader2 className="size-6 text-[#f97316] animate-spin" />,
    title: 'Processing Your Pitch',
    description: 'Processing your pitch recording and generating evaluations.',
    variant: 'orange',
  },
  EVALUATION_FAILED: {
    icon: <XCircle className="size-6 text-red-400" />,
    title: 'Evaluation Failed',
    description: 'Something went wrong while generating the evaluation.',
    variant: 'red',
  },
  FAILED: {
    icon: <XCircle className="size-6 text-red-400" />,
    title: 'Session Failed',
    description: 'Something went wrong while preparing your pitch session. Please try again.',
    variant: 'red',
  },
  INCOMPLETE: {
    icon: <AlertTriangle className="size-6 text-amber-400" />,
    title: 'Incomplete Session',
    description:
      'This session could not be fully evaluated due to missing elements such as the pitch recording or pitch deck.',
    variant: 'amber',
  },
}

const preparationStepConfig: Record<string, { title: string; description: string }> = {
  ANALYZING_DECK: {
    title: 'Analyzing Your Pitch Deck',
    description: 'Reading and extracting key information from your pitch deck.',
  },
  RESEARCHING: {
    title: 'Researching Your Market',
    description:
      'Conducting deep research on your team and market landscape. This can take up to 15 minutes.',
  },
  PREPARING_QUESTIONS: {
    title: 'Preparing Interview Questions',
    description: 'Generating tailored questions based on research findings.',
  },
}

export function PitchSessionDetail({
  status,
  preparationStep,
  pitchId,
  videoKey,
  videoCoverImageUrl,
  pitchDeckId,
  evaluation,
  createdAt,
  finishedAt,
  onBack,
  onCoverImageUpload,
  onCoverImageRemove,
}: PitchSessionDetailProps) {
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)

  const getVideoUrlMutation = useGetPitchVideoUrlMutation({
    onSuccess: (data) => {
      setVideoUrl(data.data.url)
    },
  })

  const handleVideoClick = () => {
    if (!videoKey) return
    getVideoUrlMutation.mutate({ path: { pitchId } })
  }

  const {
    individualEvaluations,
    collectiveMetricEvaluations,
    metricScores,
    totalScore,
    winnerJudge,
  } = extractEvaluationData(evaluation)

  // Handle COMPLETED with data ready - show full detail
  const hasVideo = !!videoKey
  const hasEvaluations = individualEvaluations.length > 0
  const evaluationStatus = evaluation?.status ?? null
  const isDataReady =
    status === 'COMPLETED' && hasVideo && hasEvaluations && evaluationStatus === 'COMPLETED'

  if (isDataReady) {
    return (
      <>
        <div className="flex flex-col gap-6">
          <ProfileSection title="Pitch Recording">
            <PitchCard
              pitchId={pitchId}
              totalScore={totalScore}
              videoCoverImageUrl={videoCoverImageUrl}
              finishedAt={finishedAt ?? createdAt}
              judgeScores={metricScores}
              winnerJudge={winnerJudge}
              pitchDeck={
                pitchDeckId
                  ? {
                      id: pitchDeckId,
                      fileName: 'Pitch Deck',
                      label: 'Pitch Deck',
                    }
                  : null
              }
              onVideoClick={handleVideoClick}
              onCoverImageUpload={onCoverImageUpload}
              onCoverImageRemove={onCoverImageRemove}
              onPitchDeckClick={(deck) =>
                setPreviewDocument({
                  id: deck.id,
                  fileName: deck.fileName,
                  mimeType: 'application/pdf',
                })
              }
            />
            {evaluation?.summary && (
              <div className="mt-4 rounded-lg border border-border bg-card p-4">
                <MarkdownContent>{evaluation.summary}</MarkdownContent>
              </div>
            )}
          </ProfileSection>

          {evaluation && <PitchAnalysisTabs evaluation={evaluation} />}

          {collectiveMetricEvaluations.length > 0 && (
            <ProfileSection title="Metrics">
              <MetricsTabs metrics={collectiveMetricEvaluations} />
            </ProfileSection>
          )}

          <ProfileSection title="Judge Evaluations">
            <JudgesAccordion evaluations={individualEvaluations} />
          </ProfileSection>
        </div>

        {previewDocument && (
          <DocumentPreviewModal
            document={previewDocument}
            open
            onOpenChange={(open) => !open && setPreviewDocument(null)}
          />
        )}

        {videoUrl && (
          <VideoPreviewModal
            videoUrl={videoUrl}
            title="Pitch Recording"
            open
            onOpenChange={(open) => !open && setVideoUrl(null)}
          />
        )}
      </>
    )
  }

  // Handle all other statuses (including COMPLETED without data ready)
  const statusKey: StatusKey = (() => {
    if (status !== 'COMPLETED') return status
    // Pitch session is COMPLETED — determine what to show based on evaluation status
    if (evaluationStatus === 'FAILED') return 'EVALUATION_FAILED'
    // No evaluation yet, evaluation pending/processing, or COMPLETED but missing video/data
    return 'PROCESSING'
  })()
  const config = statusConfig[statusKey]
  const styles = variantStyles[config.variant]
  const stepOverride =
    status === 'PENDING' && preparationStep ? preparationStepConfig[preparationStep] : null

  return (
    <div className="space-y-6">
      <div className={cn('rounded-xl border p-6 text-center', styles.border, styles.bg)}>
        <div
          className={cn(
            'mx-auto mb-4 flex size-12 items-center justify-center rounded-full',
            styles.iconBg
          )}
        >
          {config.icon}
        </div>
        <h2 className="text-lg font-semibold text-white">{stepOverride?.title ?? config.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {stepOverride?.description ?? config.description}
        </p>
      </div>
      {onBack && (
        <Button variant="ghost" onClick={onBack} className="w-full">
          Go Back
        </Button>
      )}
    </div>
  )
}
